import { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { toISO, today } from "../../lib/dateUtils";

const blank = () => ({
  name: "",
  category: "",
  date: toISO(today()),
  amount: 0,
  notes: "",
  offerId: "",
  costType: "opex",
  department: "",
});

export default function OneOffForm({ open, onClose, onSubmit, initial, offers = [], departments = [] }) {
  const [form, setForm] = useState(() => initial || blank());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) setForm(initial || blank());
    if (open) setErrors({});
  }, [open, initial]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.date) errs.date = "Required";
    if (!Number(form.amount)) errs.amount = "Cannot be zero (use negative for refunds)";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({
      ...form,
      amount: Number(form.amount),
      offerId: form.offerId || null,
      costType: form.costType || "opex",
      department: form.department || null,
    });
    onClose();
  };

  const recurringOffers = offers.filter((o) => o.type === "recurring");
  const oneOffOffers = offers.filter((o) => o.type === "oneoff");

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit one-off payment" : "Add one-off payment"}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} placeholder="e.g. Domain renewal" />
          <Input label="Category" value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="e.g. Hosting" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Date *" type="date" value={form.date || ""} onChange={(e) => update("date", e.target.value)} error={errors.date} />
          <Input label="Amount (USD) *" type="number" step="0.01" value={form.amount} onChange={(e) => update("amount", e.target.value)} error={errors.amount} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-brand-slate mb-1">Department</span>
            <select
              value={form.department || ""}
              onChange={(e) => update("department", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
            >
              <option value="">— Unassigned —</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-brand-slate mb-1">Assign to offer</span>
            <select
              value={form.offerId || ""}
              onChange={(e) => update("offerId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
            >
              <option value="">— Unassigned —</option>
              {recurringOffers.length > 0 && (
                <optgroup label="Recurring">
                  {recurringOffers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </optgroup>
              )}
              {oneOffOffers.length > 0 && (
                <optgroup label="One-off">
                  {oneOffOffers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </optgroup>
              )}
            </select>
          </label>
        </div>
        <p className="text-xs text-brand-slate -mt-1">Department for department-level rollups; offer for offer-specific rollups. Both optional.</p>

        <div>
          <span className="block text-xs font-medium text-brand-slate mb-1.5">Cost class</span>
          <div className="grid grid-cols-3 gap-2">
            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.costType === "cogs" ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20" : "border-gray-200 dark:border-[var(--border-soft)]"}`}>
              <input
                type="radio"
                name="costType"
                value="cogs"
                checked={form.costType === "cogs"}
                onChange={() => update("costType", "cogs")}
                className="mt-0.5 cursor-pointer"
              />
              <span>
                <span className="block text-xs font-semibold text-emerald-700 dark:text-emerald-300">Direct delivery (COGS)</span>
                <span className="block text-[10px] text-brand-slate">Counts in Gross Profit</span>
              </span>
            </label>
            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.costType === "opex" ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20" : "border-gray-200 dark:border-[var(--border-soft)]"}`}>
              <input
                type="radio"
                name="costType"
                value="opex"
                checked={form.costType === "opex"}
                onChange={() => update("costType", "opex")}
                className="mt-0.5 cursor-pointer"
              />
              <span>
                <span className="block text-xs font-semibold text-amber-700 dark:text-amber-300">Overhead (OPEX)</span>
                <span className="block text-[10px] text-brand-slate">Deducted to get Net Profit</span>
              </span>
            </label>
            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${form.costType === "client_budget" ? "border-purple-400 bg-purple-50 dark:bg-purple-900/20" : "border-gray-200 dark:border-[var(--border-soft)]"}`}>
              <input
                type="radio"
                name="costType"
                value="client_budget"
                checked={form.costType === "client_budget"}
                onChange={() => update("costType", "client_budget")}
                className="mt-0.5 cursor-pointer"
              />
              <span>
                <span className="block text-xs font-semibold text-purple-700 dark:text-purple-300">Client budget (pass-through)</span>
                <span className="block text-[10px] text-brand-slate">Spending client's reserved money — not your expense</span>
              </span>
            </label>
          </div>
        </div>
        <Input label="Notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : "Add payment"}</Button>
        </div>
      </form>
    </Modal>
  );
}